/**
 * Citizen notifications inbox — backed by service-notification.
 *
 * Endpoints (live):
 *   - GET  /v1/notifications/me?unreadOnly=&page=&pageSize=
 *   - POST /v1/notifications/me/:id/read
 *   - POST /v1/notifications/me/read-all
 *   - POST /v1/notifications/me/synthesize  (best-effort once-per-session)
 *
 * Read-state lives on the server (`readAt` column) — there is no localStorage
 * shim anymore. The historical synthesizer (which derived an inbox view from
 * wallet transactions + mail items client-side) was retired when the real
 * backend table shipped.
 *
 * Synthesis-on-first-load: the first call to `listMyNotifications` per
 * session fires `POST /v1/notifications/me/synthesize` so existing users
 * see their pre-launch wallet / parcel history without an out-of-band seed.
 * The flag is stashed in `sessionStorage` so a page refresh repeats the
 * back-fill (cheap — the endpoint is idempotent on `dedupeKey`).
 */
import { requestJson } from '../api-client';
import { env } from '../env';

// ---------------------------------------------------------------------------
// Types — preserved for callers (CustomerNotifications, useUnreadNotificationCount).
// ---------------------------------------------------------------------------

export type NotificationKind = 'milestone' | 'wallet' | 'kyc' | 'system';

export interface NotificationItem {
  readonly id: string;
  readonly kind: NotificationKind;
  readonly title: string;
  readonly body: string;
  readonly deepLink: string;
  readonly createdAt: string;
  readonly read: boolean;
  /** Optional context for shipment notifications. */
  readonly trackingNumber?: string;
}

export interface ListMyNotificationsParams {
  readonly pageSize?: number;
  readonly bearerToken?: string;
  readonly customerId?: string;
}

export interface ListMyNotificationsResponse {
  readonly items: readonly NotificationItem[];
  readonly unreadCount: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

interface BackendListResponse {
  readonly items: readonly NotificationItem[];
  readonly unreadCount: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

// ---------------------------------------------------------------------------
// Synthesis-on-first-load helper
// ---------------------------------------------------------------------------

const SYNTH_FLAG_PREFIX = 'upmp.customer.notifications.synth.';

function shouldSynth(customerId: string): boolean {
  if (typeof window === 'undefined') return false;
  if (!customerId) return false;
  try {
    return window.sessionStorage.getItem(SYNTH_FLAG_PREFIX + customerId) === null;
  } catch {
    return false;
  }
}

function markSynthDone(customerId: string): void {
  if (typeof window === 'undefined') return;
  if (!customerId) return;
  try {
    window.sessionStorage.setItem(SYNTH_FLAG_PREFIX + customerId, '1');
  } catch {
    /* sessionStorage unavailable — best-effort */
  }
}

async function fireSynthesize(bearerToken: string | undefined): Promise<void> {
  if (!bearerToken) return;
  try {
    await requestJson({
      method: 'POST',
      baseUrl: env.serviceNotificationBaseUrl,
      path: '/v1/notifications/me/synthesize',
      body: {},
      bearerToken,
    });
  } catch {
    /* best-effort — UI must not block on synthesis */
  }
}

// ---------------------------------------------------------------------------
// Public API — same surface the old synthesizer exposed.
// ---------------------------------------------------------------------------

export async function listMyNotifications(
  params: ListMyNotificationsParams = {},
): Promise<ListMyNotificationsResponse> {
  const pageSize = params.pageSize ?? 20;
  const bearerToken = params.bearerToken;
  const customerId = params.customerId ?? '';

  // First-load synthesis (await so the very first paint includes the
  // back-filled rows; subsequent loads short-circuit on the session flag).
  if (shouldSynth(customerId)) {
    await fireSynthesize(bearerToken);
    markSynthDone(customerId);
  }

  const qs = new URLSearchParams();
  qs.set('page', '1');
  qs.set('pageSize', String(pageSize));

  const res = await requestJson<BackendListResponse>({
    method: 'GET',
    baseUrl: env.serviceNotificationBaseUrl,
    path: `/v1/notifications/me?${qs.toString()}`,
    ...(bearerToken ? { bearerToken } : {}),
  });

  return {
    items: res.items,
    unreadCount: res.unreadCount,
    total: res.total,
  };
}

/**
 * Mark a single notification as read on the backend.
 *
 * The `customerId` argument is preserved for caller-compatibility with the
 * old localStorage shim but is no longer needed — the backend resolves the
 * citizen identity from the JWT.
 */
export async function markNotificationRead(
  id: string,
  _customerId: string,
  bearerToken?: string,
): Promise<void> {
  void _customerId;
  await requestJson<{ ok: boolean }>({
    method: 'POST',
    baseUrl: env.serviceNotificationBaseUrl,
    path: `/v1/notifications/me/${encodeURIComponent(id)}/read`,
    body: {},
    ...(bearerToken ? { bearerToken } : {}),
  });
}

/** Mark every unread notification as read on the backend. */
export async function markAllNotificationsRead(
  _ids: readonly string[],
  _customerId: string,
  bearerToken?: string,
): Promise<void> {
  void _ids;
  void _customerId;
  await requestJson<{ updated: number }>({
    method: 'POST',
    baseUrl: env.serviceNotificationBaseUrl,
    path: '/v1/notifications/me/read-all',
    body: {},
    ...(bearerToken ? { bearerToken } : {}),
  });
}

/** Reset the per-session synthesis flag — exposed for tests. */
export function clearReadState(customerId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SYNTH_FLAG_PREFIX + customerId);
  } catch {
    /* best-effort */
  }
}
